import { DirectDataInputSearchCriteria } from "domain/entity/DirectDataInput/DirectDataInputSearchCriteria";
import { DirectDataInputConstant } from "presentation/constant/DirectDataInput/DirectDataInputConstant";
import { directDateInputStatusDropdownOption, masterTypeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { PortConstant } from "presentation/constant/Port/PortConstant";
import { useDirectDataInputVM } from "presentation/hook/DirectDataInput/useDirectDataInputVM";
import { useDirectDataInputTracked } from "presentation/store/DirectDataInput/DirectDataInputProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const DirectDataInputSearchPanel = () => {
    const SCREEN_CONSTANT = DirectDataInputConstant;
    const [directDataInputState] = useDirectDataInputTracked();
    const directDataInputVM = useDirectDataInputVM();
    const { searchCriteria } = directDataInputState;
    const [isLoading, setIsLoading] = useState(false);

    const resetButtonClicked = useCallback(() => {
        directDataInputVM.onSearchCriteriaResetClick();
    }, [directDataInputVM]);

    const searchButtonClicked = useCallback((searchCriteria: DirectDataInputSearchCriteria) => {
        setIsLoading(true);
        directDataInputVM.setIsSearchLoading(true);
        directDataInputVM.onSearch(searchCriteria).then((data) => {
            setIsLoading(false);
            directDataInputVM.setIsSearchLoading(false);
        }).catch(error => {
            setIsLoading(false);
            directDataInputVM.setIsSearchLoading(false);
        })

    }, [directDataInputVM])

    const memoMasterType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.masterType || ''}
                fieldValue={searchCriteria?.masterType}
                fieldLabel={SCREEN_CONSTANT.MASTER_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'masterType'}
                maxLength={60}
                sort={false}
                options={masterTypeDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    directDataInputVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.masterType, SCREEN_CONSTANT.MASTER_TYPE, directDataInputVM])

    const memoForwarderCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.forwarderCode || ''}
                fieldValue={searchCriteria?.forwarderCode}
                fieldLabel={SCREEN_CONSTANT.FORWARDER_SHIPPER_CONSIGNEE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'forwarderCode'}
                maxLength={60}
                sort={false}
                options={directDataInputState.dynamicOptions.forwarderCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    directDataInputVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.forwarderCode, SCREEN_CONSTANT.FORWARDER_SHIPPER_CONSIGNEE, directDataInputState.dynamicOptions.forwarderCodeDropdownOptions, directDataInputVM])

    const memoDamageType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.damageType || ''}
                fieldValue={searchCriteria?.damageType}
                fieldLabel={SCREEN_CONSTANT.DAMAGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'damageType'}
                maxLength={60}
                sort={false}
                options={directDataInputState.dynamicOptions.damageTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    directDataInputVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.damageType, SCREEN_CONSTANT.DAMAGE_TYPE, directDataInputState.dynamicOptions.damageTypeDropdownOptions, directDataInputVM])

    const memoStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.status || ''}
                fieldValue={searchCriteria?.status}
                fieldLabel={SCREEN_CONSTANT.STATUS}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'status'}
                maxLength={60}
                sort={false}
                options={directDateInputStatusDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    directDataInputVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [searchCriteria?.status, SCREEN_CONSTANT.STATUS, directDataInputVM])

    const memoOpsDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "20px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.OPS_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "opsDateFrom", endField: "opsDateTo" }}
                dateRange={{ startDate: searchCriteria.opsDateFrom, endDate: searchCriteria.opsDateTo }}
                onDatesChange={directDataInputVM.onDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.OPS_DATE_RANGE, directDataInputVM.onDateRangeChange, searchCriteria.opsDateFrom, searchCriteria.opsDateTo])


    const memoTruckerName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.truckerName ?? ''}
                fieldValue={searchCriteria?.truckerName}
                fieldLabel={SCREEN_CONSTANT.TRUCKER_NAME}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'truckerName'}
                maxLength={30}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    directDataInputVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.TRUCKER_NAME, directDataInputVM, searchCriteria?.truckerName])

    const memoCnicNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.cnicNo ?? ''}
                fieldValue={searchCriteria?.cnicNo}
                fieldLabel={SCREEN_CONSTANT.CNIC_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'cnicNo'}
                maxLength={15}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    directDataInputVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.CNIC_NO, directDataInputVM, searchCriteria?.cnicNo])

    const memoVehicleNoo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.vehicleNo ?? ''}
                fieldValue={searchCriteria?.vehicleNo}
                fieldLabel={SCREEN_CONSTANT.VEHICLE_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'vehicleNo'}
                maxLength={10}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    directDataInputVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.VEHICLE_NO, directDataInputVM, searchCriteria?.vehicleNo])

    return (
        <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{PortConstant.SEARCH_CRITERIA}</SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={directDataInputVM.onSearchClick} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>

                        <CriteriaItemContainer>
                            {memoMasterType}
                            {memoStatus}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoDamageType}
                            {memoForwarderCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoOpsDateFromTo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoTruckerName}
                            {memoCnicNo}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoVehicleNoo}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(searchCriteria)} />
            </SidebarActionBar>
        </div>
    );
}

export default DirectDataInputSearchPanel;
